<template>
  <transition
    name="fade"
    @before-enter="onBeforeShow"
    @after-leave="onAfterHide"
  >
    <div v-if="show" ref="sanitasSearch" class="sanitas-search">
      <div class="sanitas-search__close" @click="hideSearch">
        <svg
          class="sanitas-search__close__icon"
          width="16px"
          height="16px"
          viewBox="0 0 16 16"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
        >
          <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
            <g
              transform="translate(-1532.000000, -36.000000)"
              fill="#5BAC26"
              fill-rule="nonzero"
            >
              <g id="xmark" transform="translate(1532.499999, 36.498828)">
                <path
                  fill="currentColor"
                  d="M14.559376,12.4417963 C15.1453135,13.0277338 15.1453135,13.9769526 14.559376,14.5628901 C14.2687504,14.8558588 13.8843763,15.0011716 13.5,15.0011716 C13.1156251,15.0011716 12.732188,14.8546872 12.4396884,14.5617185 L7.5,9.62460852 L2.56078219,14.5605469 C2.26781344,14.8558588 1.88390708,15.0011716 1.5,15.0011716 C1.11609435,15.0011716 0.732656479,14.8558588 0.439453125,14.5605469 C-0.146484375,13.9746094 -0.146484375,13.0253906 0.439453125,12.4394531 L5.38007855,7.49882841 L0.439453125,2.56054688 C-0.146484375,1.97460938 -0.146484375,1.02539063 0.439453125,0.439453125 C1.02539063,-0.146484375 1.97460938,-0.146484375 2.56054688,0.439453125 L7.5,5.38242173 L12.4406247,0.441796303 C13.0265622,-0.144141197 13.975781,-0.144141197 14.5617185,0.441796303 C15.147656,1.0277338 15.147656,1.97695255 14.5617185,2.56289005 L9.62109375,7.50351477 L14.5593753,12.4417963 L14.559376,12.4417963 Z"
                ></path>
              </g>
            </g>
          </g>
        </svg>
      </div>

      <div class="sanitas-search__container">
        <div class="sanitas-search__form">
          <div class="search-container">
            <h2 class="search__title">{{ $t('title') }}</h2>

            <SanitasFormSearch
              class="sanitas-form-search"
              is-negative
              :autocomplete-search-endpoint-url="autoCompleteUrl"
              query-search-endpoint-method="GET"
              :minimum-search-length="3"
              :should-show-results="hasResults"
              :max-visible-suggestions="maxVisibleSuggestions"
              :show-loading="showLoading"
              :search-term="searchTerm"
              @submit="onSubmit"
              @suggestionsOpened="val => (suggestionsOpened = val)"
            >
              <slot />
            </SanitasFormSearch>

            <p v-if="hasError" class="error">
              {{ $t('search-error-message') }}
            </p>

            <template v-if="!showLoading">
              <p
                v-if="
                  (searchTerm.length > 0 && !hasResults) ||
                  shouldShowSpellcheckMessage
                "
                class="search__noresult"
              >
                {{ $t('noresults-found-message-part1') }}{{ searchTerm
                }}{{ $t('noresults-found-message-part2') }}
              </p>
              <p v-if="shouldShowSpellcheckMessage" class="search__spellcheck">
                {{ $t('spellcheck-info') }}
                <span class="search__spellcheck-term">{{ spellcheck }}</span
                >:
              </p>
            </template>
          </div>

          <SanitasSearchList
            v-if="hasResults"
            :class="{ 'is-open': suggestionsOpened }"
            :results="searchResults"
            :category-list="categoryList"
            :nice-category-list="niceCategoryList"
            :lists-by-category="listsByCategory"
            :last-active-category="lastActiveCategory"
            @sanitassearchsetlastcategory="setLastCategory"
          />

          <Loading v-if="showLoading" />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { provideI36n, useI36n } from '@jota-one/i36n/src/index.vue2'

import { client } from '@/helpers/http'
import { getScrollbarWidth } from '@/helpers/scrollbar'
import { trackAction, tracker } from '@/helpers/tracker'

import Loading from './components/Loading'
import SanitasFormSearch from './components/SanitasFormSearch'
import SanitasSearchList from './components/SanitasSearchList'

function compareCategories(a, b) {
  if (!a || !b) {
    return 0
  }
  if (a.length < 3 || b.length < 3) {
    return 0
  }

  // Remove the language prefixes for the remaining tests.
  a = a.substring(2)
  b = b.substring(2)

  const orderingvalue = {
    all: 1,
    privatkunden: 2,
    uebersanitas: 3,
    magazin: 4,
    healthforecast: 5,
  }

  if (orderingvalue[a] < orderingvalue[b]) {
    return -1
  }
  if (orderingvalue[a] > orderingvalue[b]) {
    return 1
  }
  return 0
}

function groupBy(list, property) {
  return list.reduce((memo, x) => {
    let key = x[property]
    memo[key] = memo[key] || []
    memo[key].push(x)
    return memo
  }, {})
}

export default {
  name: 'SanitasSearchModule',

  components: {
    SanitasSearchList,
    SanitasFormSearch,
    Loading,
  },

  props: {
    /**
     * Array of retention Related Terms
     * [ 'String_1', 'String_2', 'String_N' ]
     */
    retentionRelatedTerms: {
      type: Array,
      default: () => [],
    },
    /**
     * url that will be queried in order to get the auto-complete suggestions.
     *
     * The endpoint response has to be an array of `String`:
     * ```
     * [ 'String_1', 'String_2', 'String_N' ]
     * ```
     */
    autoCompleteUrl: {
      type: String,
      default: '',
    },

    /**
     * url that will be queried in order to get the search results.
     *
     * The endpoint response needs to have the structure detailed in
     * the `sanitas-search-list` Polymer component
     */
    searchUrl: {
      type: String,
      default: '',
    },

    /**
     * Maximum number of visible suggestions in the dropdown
     */
    maxVisibleSuggestions: {
      type: Number,
      default: 3,
    },
  },

  setup() {
    const language = window.Sanitas.language || 'de'

    const loadLabels = lang => import(`./i18n/${lang}.json`)
    provideI36n(language, { load: loadLabels })
    const { $label } = useI36n()

    return {
      $t: $label,
    }
  },

  data: () => ({
    searchTerm: '',
    hasError: false,
    showLoading: false,
    suggestionsOpened: false,
    show: false,
    searchResults: { empty: true },
    categoryList: [],
    niceCategoryList: [],
    listsByCategory: {},
    lastActiveCategory: '',
    language: 'de',
    spellcheck: '',
  }),

  computed: {
    hasResults() {
      return this.searchResults?.articles?.length > 0
    },
    shouldShowSpellcheckMessage() {
      return (
        this.searchResults?.articles_spellcheck &&
        this.searchResults?.articles?.length > 0
      )
    },
  },

  watch: {
    show(value) {
      this.searchResults = { empty: true }
      this.showLoading = false
    },
  },

  created() {
    this.scrollbarWidth = getScrollbarWidth()
    window.addEventListener('sanitas-open-search-view', this.showSearch)
  },
  mounted() {
    this.language = window.Sanitas.language || 'de'

    const url = new URL(window.location)
    if (url.searchParams.get('q')) {
      this.searchTerm = url.searchParams.get('q')
      this.showSearch()
    }

    window.addEventListener('popstate', this.popstatehandler)
  },
  destroyed() {
    window.removeEventListener('sanitas-open-search-view', this.showSearch)
  },

  methods: {
    showSearch() {
      this.show = true
    },
    hideSearch() {
      this.show = false
      this.searchTerm = ''

      const url = new URL(window.location)
      if (url.searchParams.get('q')) {
        url.searchParams.delete('q')
        window.history.replaceState({}, '', url)
      }
    },
    onBeforeShow() {
      Object.assign(document.body.style, {
        overflowY: 'hidden',
        // The margin is needed to replace the space the scrollbar was taking
        // up. This is usually done through padding, but we already have a
        // padding on the body.
        marginRight: this.scrollbarWidth + 'px',
      })
    },
    onAfterHide() {
      Object.assign(document.body.style, {
        overflowY: 'scroll',
        marginRight: '',
      })
    },

    async onSubmit({ value }) {
      if (this.showLoading) {
        return
      }
      this.searchTerm = value
      if (value === undefined || value === '') {
        this.searchResults = undefined
        return
      }
      if (value.length < 3) {
        this.searchResults = undefined
        return
      }

      let retental = -1
      if (this.retentionRelatedTerms) {
        this.retentionRelatedTerms.some((element, index) => {
          if (element.toLowerCase() === value.toLowerCase()) {
            retental = index
            return true
          }
        })
      }
      if (this.retentionRelatedTerms && retental > -1) {
        window.dispatchEvent(
          new CustomEvent('sanitas_chat-trigger_nudging', {
            detail: { specialCase: 'retention' },
          }),
        )
      }

      this.showLoading = true
      this.hasError = false
      this.searchResults = undefined
      this.searchTerm = value

      value = value.substring(0, 100)
      value = value.replace(/[^0-9a-zA-Z’àâçéèêëîïôûùüÿñæœìòóäöáíú¿«ãõ ]/gi, '')

      try {
        let { data } = await client().get(this.searchUrl, {
          params: {
            q: value,
            lang: this.language,
            _charset_: 'utf-8',
          },
        })

        this.searchResults = data
        this.spellcheck = this.searchResults.articles_spellcheck
        this.hasError = false
        this.computeCategoryAndCategoryLists()

        let trackvalue = value
        if (this.spellcheck) {
          trackvalue = value + '->' + this.spellcheck
        }

        if (this.searchResults?.articles?.length > 0) {
          tracker.set({
            data: {
              search: {
                formId: 'global',
                query: trackvalue,
                resultsCount: this.searchResults.articles.length,
              },
            },
          })
          trackAction('ComSearchSubmitted')
        } else {
          tracker.set({
            data: {
              search: {
                formId: 'global',
                query: trackvalue,
                resultsCount: 0,
              },
            },
          })
          trackAction('ComSearchSubmittedNoResult')
        }

        const url = new URL(window.location)
        if (url.searchParams.get('q')) {
          url.searchParams.set('q', value)
          window.history.replaceState({}, '', url)
        } else {
          url.searchParams.set('q', value)
          window.history.pushState({}, '', url)
        }
      } catch (error) {
        this.hasError = true
      } finally {
        this.showLoading = false
      }
    },
    setLastCategory(value) {
      this.lastActiveCategory = value
    },
    computeCategoryAndCategoryLists() {
      const categories = this.searchResults.articles
        .map(item => item.category)
        .filter((value, index, list) => list.indexOf(value) === index)
        .filter(name => name !== 'NOCATEGORY')
        .filter(Boolean)

      categories.unshift(this.language + 'all')
      categories.sort(compareCategories)

      this.categoryList = categories

      const articleslist = this.searchResults.articles

      var grouped = groupBy(articleslist, 'category')
      grouped[this.language + 'all'] = JSON.parse(JSON.stringify(articleslist))
      this.listsByCategory = grouped

      this.niceCategoryList = this.generateNiceCategoryList()
    },
    generateNiceCategoryList() {
      let categoryLabels = {
        deall: 'Alle',
        frall: 'Tout',
        itall: 'Tutto',
        enall: 'All',

        deprivatkunden: 'Privatkunden',
        frprivatkunden: 'Clients privés',
        itprivatkunden: 'Clienti privati',
        enprivatkunden: 'Private customers',

        deuebersanitas: 'Über Sanitas',
        fruebersanitas: 'À propos de Sanitas',
        ituebersanitas: 'Chi è Sanitas',
        enuebersanitas: 'About Sanitas',

        demagazin: 'Magazin',
        frmagazin: 'Magazine',
        itmagazin: 'Rivista',
        enmagazin: 'Magazine',

        dehealthforecast: 'Health Forecast',
        frhealthforecast: 'Health Forecast',
        ithealthforecast: 'Health Forecast',
        enhealthforecast: 'Health Forecast',

        NOCATEGORY: 'NOCATEGORY',
      }

      let niceCats = this.categoryList.reduce((all, current) => {
        all[current] = categoryLabels[current] || current + 'NICENAMENOTFOUND'
        return all
      }, {})

      return niceCats
    },
    popstatehandler() {
      const url = new URL(window.location)
      if (url.searchParams.get('q')) {
        let query = url.searchParams.get('q')
        this.showSearch()
        this.searchTerm = query
      } else {
        this.hideSearch()
      }
    },
  },
}
</script>

<style lang="postcss" scoped>
.sanitas-search {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  height: 100%;
  z-index: 9999;
  background-color: var(--c-primary-neutral-3);
  overflow-y: scroll;
  margin: 0;

  &__close {
    position: absolute;
    right: var(--spacing-2);
    top: var(--spacing-2);
    cursor: pointer;
    padding: var(--spacing-2);

    &__icon {
      color: var(--c-primary-color-3);

      &:hover {
        color: var(--c-primary-color-2);
      }
    }
  }

  &__form {
    margin: 0 0 56px 0;

    padding: 15px;
    .search-container {
      padding-top: 25px;
    }

    .search__title {
      font-weight: 400;
      font-style: normal;
      font-size: 24px;
      line-height: 1.2;
      letter-spacing: -0.6px;
      color: var(--c-primary-neutral-1) !important;
      margin-bottom: 28px;
    }

    .sanitas-form-search {
      position: relative;
      width: 100%;
    }

    .error {
      font-weight: 400;
      font-style: normal;
      font-size: 13px;
      line-height: 1.35;
      letter-spacing: 0.5px;
      color: var(--c-secondary-color-1);

      margin-bottom: 0;
    }

    .search__noresult {
      font-weight: 700;
      font-size: 20px;
      line-height: 1.2;
      color: var(--c-primary-neutral-1);
      margin-top: 56px;
    }

    .search__spellcheck {
      font-weight: 400;
      font-size: 16px;
      line-height: 1.5;
      color: var(--c-primary-neutral-1);
      margin-top: var(--spacing-4);
    }

    .search__spellcheck-term {
      font-weight: 700;
      color: var(--c-primary-color-2);
    }

    .search-list {
      margin-top: 48px;
    }
  }
}

@media (--md) {
  .sanitas-search {
    &__form {
      margin: 0 100px 56px 100px;
      .search__title {
        font-size: 50px;
        margin: 0 0 56px 0;
      }
    }
  }
}

@media (--xl) {
  .sanitas-search__container {
    display: flex;
    justify-content: center;
  }
  .sanitas-search {
    &__form {
      width: 864px;
      .search__title {
        font-size: 50px;
        margin: 0 0 56px 0;
      }
    }
  }
}

.fade {
  &-enter,
  &-leave-to {
    opacity: 0;
  }
  &-enter-active,
  &-leave-active {
    transition: opacity 0.6s;
  }
}
</style>
